import { BetslipAnalyticsApi } from 'api';
import { ActionEventType, ActionType, AnalyticalItem, AnalyticalItemAction } from 'api/betslipAnalytics.api';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { BESTLIP_ANALYTICS_TIMER } from 'shared/constants/common';
import { BETSType, EventType, State } from 'shared/interface';
import { clearInactivityTimer, startInactivityTimer } from 'shared/util/timer';
import { findCookieValue, getUserDetails } from 'shared/util/utility';
import { v4 as uuidv4 } from 'uuid';

export const BETSLIP_ANALYTICS_KEY = 'betslipAnalytics';
const SESSION_ID_KEY = 'sessionId';

export const generateSessionId = () => {
	const sessionId = localStorage.getItem(SESSION_ID_KEY);

	if (!sessionId) {
		const newSessionId = uuidv4();
		localStorage.setItem(SESSION_ID_KEY, newSessionId);

		return newSessionId;
	}

	return sessionId;
};

export const sendAnalytics = async (data: AnalyticalItem) => {
	try {
		const response = await BetslipAnalyticsApi.postBetslipAnalytics(data);
		if (!response.data.isError) {
			removeBetslipAnalytics(data);
		}
	} catch (error) {
		console.error('Analytics error:', error);
	}
};

const getUnsentAnalytics = (localAnalytics: AnalyticalItem, responseAnalytics: AnalyticalItem) => {
	const filteredActions = localAnalytics.action.filter((localAnalayticsAction: AnalyticalItemAction) => {
		return !responseAnalytics.action.some(
			(responseAnalyticsAction: AnalyticalItemAction) => responseAnalyticsAction.action_id === localAnalayticsAction.action_id,
		);
	});
	return {
		...localAnalytics,
		action: filteredActions,
	};
};

const removeBetslipAnalytics = (data: AnalyticalItem) => {
	const localItems = localStorage.getItem(BETSLIP_ANALYTICS_KEY);
	const objLocalItems = localItems ? JSON.parse(localItems) : {};
	localStorage.setItem(BETSLIP_ANALYTICS_KEY, JSON.stringify(getUnsentAnalytics(objLocalItems, data)));
};

export const useBetslipAnalytics = () => {
	const bets = useSelector<State, BETSType>((state) => state.coupon.bets);
	const isBetsEmpty = bets.outcomes.length === 0;

	const saveToLocalStorage = (item: any) => {
		const userOddsSession = findCookieValue('x-odds-session');
		const sessionId = localStorage.getItem(SESSION_ID_KEY);
		const userId = getUserDetails()?.userId;

		localStorage.setItem(BETSLIP_ANALYTICS_KEY, JSON.stringify({ ...item, userId, sessionId, userOddsSession }));
	};

	const loadFromLocalStorage = (): AnalyticalItem => {
		const storedItems = localStorage.getItem(BETSLIP_ANALYTICS_KEY);
		return storedItems ? JSON.parse(storedItems) : { sessionId: 0, userOddsSession: '', action: [] };
	};

	const addAction = useCallback((action_data: any, origin_url: string, action_type: ActionType) => {
		const storedItems = loadFromLocalStorage();
		const event_count = storedItems.action.length + 1;
		const event_type = action_data.isLive ? ActionEventType.LIVE : ActionEventType.PREMATCH;
		const action_at = new Date().getTime();
		const isVirtualEvent = [
			EventType.VIRTUAL_FOOTBALL,
			EventType.VIRTUAL_HORSE_RACING,
			EventType.VIRTUAL_DOG_RACING,
			EventType.VIRTUAL_BASKETBALL,
		].includes(action_data.eventType);

		const newItem: AnalyticalItemAction = {
			action_at,
			action_type,
			action_id: uuidv4(),
			action_data: {
				bet_selected_1: action_data.gameId,
				bet_selected_2: action_data.outcomeId,
				bet_selected_3: action_data.gameType,
				event_category_1: action_data.sportId,
				event_category_2: action_data.categoryId,
				event_id: action_data.eventId,
				odds: action_data.outcomeOdds,
				participants: action_data.eventName,
				event_count,
				event_type,
				origin_url,
			},
		};
		const matchedOutcome = bets.outcomes.find((item) => Number(item.outcomeId) === newItem.action_data.bet_selected_2);
		const matchedEventId = bets.outcomes.find((item) => Number(item.eventId) === newItem.action_data.event_id);

		const checkAndUpdateActions = storedItems.action.map((item: AnalyticalItemAction) => {
			if (item.action_data.event_id === newItem.action_data.event_id) {
				return {
					...item,
					action_type: ActionType.REMOVE,
				};
			}
			return item;
		});

		const removePreviouslyAddedAction = {
			action_at,
			action_type: ActionType.REMOVE,
			action_id: uuidv4(),
			action_data: {
				bet_selected_1: action_data.gameId,
				bet_selected_2: action_data.outcomeId,
				event_category_1: action_data.sportId,
				event_category_2: action_data.categoryId,
				event_id: action_data.eventId,
				odds: action_data.outcomeOdds,
				participants: action_data.eventName,
				event_count,
				event_type,
				origin_url,
			},
		};

		const additionalActions = matchedEventId && !matchedOutcome && !isBetsEmpty ? [removePreviouslyAddedAction] : [];

		const updatedItems = {
			...storedItems,
			action: [...checkAndUpdateActions, ...additionalActions, newItem],
		};

		!isVirtualEvent && saveToLocalStorage(updatedItems);

		startInactivityTimer(() => {
			const unsentData = localStorage.getItem(BETSLIP_ANALYTICS_KEY);
			if (unsentData) {
				clearInactivityTimer();
				sendAnalytics(JSON.parse(unsentData));
			}
		}, Number(BESTLIP_ANALYTICS_TIMER));
	}, []);

	return { addAction, loadFromLocalStorage, saveToLocalStorage };
};
